import { useEffect } from 'react';
import { Object3D } from 'three';

import {
  SelectControls as SelectControlsBase,
  SelectControlsProps as SelectControlsPropsBase,
  useDollhouse,
  useSelection,
} from '@spazious/dollhouse-engine';
import { useStore } from '@spazious/dollhouse-store';
import { listenObjectChanges } from '@spazious/dollhouse-three';
import { ControlsModeEnum, ToolModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

import { useSelectionReducer } from '../../hooks/index.js';
import { useFeatures } from '../Features/useFeatures.js';

export interface SelectControlsProps extends Omit<SelectControlsPropsBase, 'reducer' | 'shift'> {}

export const SelectControls: React.FC<SelectControlsProps> = ({
  children,
  enabled = true,
  ...restProps
}) => {
  const scene = useDollhouse(({ scene }) => scene);
  const { selected, select } = useSelection();
  const { hasFeature } = useFeatures();

  const isSelectTool = useStore(({ tool }) => tool === ToolModeEnum.Select);
  const isNumberingTool = useStore(({ tool }) => tool === ToolModeEnum.Numbering);
  const lookMode = useStore(({ controls }) => controls === ControlsModeEnum.Walk);

  const reducer = useSelectionReducer();

  useEffect(() => {
    // reset selection in walk mode
    if (lookMode) {
      select([]);
    }
  }, [lookMode, select]);

  const nodeAttrsEditable = hasFeature('graphs.setup.node.attrs:editable');
  const selectOriginator = !isSelectTool && !isNumberingTool && nodeAttrsEditable;

  // remove selection when the node is removed from scene
  const removedNode = useEventCallback((node: Object3D) => {
    if (selected.includes(node)) {
      select(selected.filter((n) => n !== node));
    }
  });

  useEffect(
    () => (scene ? listenObjectChanges(scene, () => {}, removedNode) : () => {}),
    [removedNode, scene]
  );

  return (
    <SelectControlsBase
      enabled={enabled && !lookMode}
      reducer={(...args) => reducer(...args, selectOriginator)}
      shift={isSelectTool}
      {...restProps}
    >
      {children}
    </SelectControlsBase>
  );
};
